import React, { useState, useMemo } from "react";
import { formatUSD } from "../../../lib/api";
import { MagnifyingGlass, CaretDown, CaretRight, CreditCard, Warning } from "@phosphor-icons/react";

/**
 * AgentManagementTable — searchable + expandable agent dashboard rendered
 * above the legacy Commission table on /admin/agents. Columns focus on
 * card-issuance volume (today / week / month counts) and total commission.
 *
 * Props:
 *   - rows: agent summary rows from /api/admin/agent-commissions/summary
 */
const isMissingBank = (r) => !((r.bank_account_number || "").trim() && (r.routing_number || "").trim());

export function AgentManagementTable({ rows, onSelectAgent }) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // {agent_id: bool}
  const [sortKey, setSortKey] = useState("cards_month");
  const [missingBankOnly, setMissingBankOnly] = useState(false);

  const missingBankCount = useMemo(
    () => rows.filter(isMissingBank).length,
    [rows],
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = rows;
    if (s) {
      list = list.filter(r =>
        (r.full_name || "").toLowerCase().includes(s) ||
        (r.email || "").toLowerCase().includes(s) ||
        (r.agent_code || "").toLowerCase().includes(s) ||
        (r.phone || "").toLowerCase().includes(s)
      );
    }
    if (missingBankOnly) {
      list = list.filter(isMissingBank);
    }
    // Sort descending by sortKey
    return [...list].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
  }, [rows, search, sortKey, missingBankOnly]);

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  return (
    <section className="mt-12" data-testid="agent-management-section">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="serif text-3xl">Agent management</h2>
          <p className="text-[#4A5C50] text-xs mt-1">
            Card-issuance activity per agent. Click a row to expand details. Sortable + searchable.
            {missingBankCount > 0 && (
              <button
                type="button"
                onClick={() => setMissingBankOnly(v => !v)}
                className={`ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] tracking-widest uppercase font-semibold transition ${
                  missingBankOnly
                    ? "bg-amber-100 border-amber-400 text-amber-800"
                    : "bg-white border-amber-300 text-amber-700 hover:bg-amber-50"
                }`}
                data-testid="missing-bank-filter-toggle"
                title="Show only agents missing bank info"
              >
                <Warning size={11} weight="fill" />
                {missingBankCount} missing bank info {missingBankOnly && "· clear filter"}
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5C50]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="pl-9 pr-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#184A30]"
              data-testid="agent-management-search"
            />
          </div>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
            data-testid="agent-management-sort"
          >
            <option value="cards_today">Sort: Today (cards)</option>
            <option value="cards_week">Sort: Week (cards)</option>
            <option value="cards_month">Sort: Month (cards)</option>
            <option value="lifetime_total">Sort: Total commission</option>
            <option value="cards_lifetime">Sort: Lifetime (cards)</option>
          </select>
        </div>
      </div>

      <div className="bg-white border border-[#E5E0D8] rounded-2xl mt-4 overflow-x-auto" data-testid="agent-management-table">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="text-left text-[#4A5C50] text-[11px] uppercase tracking-widest">
            <tr>
              <th className="p-4 w-10"></th>
              <th className="p-4">Agent</th>
              <th className="p-4 text-right">
                <CreditCard size={12} className="inline mr-1" /> Today
              </th>
              <th className="p-4 text-right">Week</th>
              <th className="p-4 text-right">Month</th>
              <th className="p-4 text-right">Total commission</th>
              <th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-10 text-center text-[#4A5C50]" data-testid="agent-management-empty">
                  {search ? `No agents match "${search}"` : "No agents yet."}
                </td>
              </tr>
            ) : filtered.map((r) => {
              const isOpen = !!expanded[r.agent_id];
              return (
                <React.Fragment key={r.agent_id}>
                  <tr
                    className="border-t border-[#E5E0D8] hover:bg-stone-50 cursor-pointer transition-colors"
                    onClick={() => toggle(r.agent_id)}
                    data-testid={`mgmt-row-${r.agent_code}`}
                  >
                    <td className="p-4">
                      {isOpen
                        ? <CaretDown size={16} weight="bold" className="text-[#184A30]" data-testid={`mgmt-caret-open-${r.agent_code}`} />
                        : <CaretRight size={16} weight="bold" className="text-[#4A5C50]" data-testid={`mgmt-caret-closed-${r.agent_code}`} />
                      }
                    </td>
                    <td className="p-4">
                      <div className="font-semibold text-[#112318] flex items-center gap-2">
                        {r.full_name || "—"}
                        {isMissingBank(r) && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[9px] tracking-widest uppercase font-semibold px-1.5 py-0.5"
                            data-testid={`mgmt-missing-bank-${r.agent_code}`}
                            title="Bank info not set — payouts cannot be processed yet"
                          >
                            <Warning size={9} weight="fill" /> No bank
                          </span>
                        )}
                      </div>
                      {onSelectAgent ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onSelectAgent(r.agent_id); }}
                          className="text-xs text-[#184A30] hover:text-[#0d3320] underline decoration-dotted underline-offset-4 mono text-left"
                          data-testid={`mgmt-email-link-${r.agent_code}`}
                          title="View commission detail · Week / Month / Year"
                        >
                          {r.email}
                        </button>
                      ) : (
                        <div className="text-xs text-[#4A5C50] mono">{r.email}</div>
                      )}
                      {r.agent_code && (
                        <div className="text-[10px] tracking-widest uppercase text-[#4A5C50] mt-0.5">
                          Code <span className="mono font-semibold text-[#184A30]">{r.agent_code}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-right mono font-semibold" data-testid={`mgmt-today-${r.agent_code}`}>
                      {r.cards_today ?? 0}
                    </td>
                    <td className="p-4 text-right mono" data-testid={`mgmt-week-${r.agent_code}`}>
                      {r.cards_week ?? 0}
                    </td>
                    <td className="p-4 text-right mono" data-testid={`mgmt-month-${r.agent_code}`}>
                      {r.cards_month ?? 0}
                    </td>
                    <td className="p-4 text-right mono font-semibold text-emerald-700" data-testid={`mgmt-commission-${r.agent_code}`}>
                      {formatUSD(r.lifetime_total ?? 0)}
                    </td>
                    <td className="p-4">
                      {r.status === "suspended" ? (
                        <span className="inline-block rounded-full bg-rose-100 text-rose-700 text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5">Suspended</span>
                      ) : (
                        <span className="inline-block rounded-full bg-emerald-100 text-emerald-700 text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5">Active</span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-stone-50" data-testid={`mgmt-detail-${r.agent_code}`}>
                      <td colSpan={7} className="p-5">
                        <div className="grid md:grid-cols-3 gap-5">
                          <DetailBlock title="Contact">
                            <DL k="Phone" v={r.phone || "—"} mono />
                            <DL k="Email" v={r.email} mono />
                            <DL k="Address" v={r.address || "—"} />
                          </DetailBlock>
                          <DetailBlock title="Bank · for payouts">
                            <DL k="Bank" v={r.bank_name || "—"} />
                            <DL k="Account holder" v={r.bank_account_holder || "—"} />
                            <DL k="Account #" v={r.bank_account_number ? `•••• ${r.bank_account_number.slice(-4)}` : "—"} mono />
                            <DL k="Routing #" v={r.routing_number ? `•••• ${r.routing_number.slice(-4)}` : "—"} mono />
                          </DetailBlock>
                          <DetailBlock title="Commission breakdown">
                            <DL k="Lifetime cards" v={r.cards_lifetime ?? 0} mono />
                            <DL k="Week" v={formatUSD(r.week_total ?? 0)} mono />
                            <DL k="Month" v={formatUSD(r.month_total ?? 0)} mono />
                            <DL k="Year" v={formatUSD(r.year_total ?? 0)} mono />
                            <DL k="Unpaid" v={formatUSD(r.unpaid_total ?? 0)} mono tone="amber" />
                            <DL k="Commission %" v={`${r.commission_percent}%`} mono />
                          </DetailBlock>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-[#E5E0D8] bg-stone-50 text-xs text-[#4A5C50] flex justify-between" data-testid="agent-management-footer">
            <span>{filtered.length} agent{filtered.length === 1 ? "" : "s"}{search ? ` matching "${search}"` : ""}</span>
            <span className="mono">Sorted by {sortKey.replace("_", " ")}</span>
          </div>
        )}
      </div>
    </section>
  );
}

function DetailBlock({ title, children }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#4A5C50] mb-2">{title}</p>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function DL({ k, v, mono, tone }) {
  const toneCls = tone === "amber" ? "text-amber-700 font-semibold" : "text-[#112318]";
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="text-xs text-[#4A5C50]">{k}</dt>
      <dd className={`${mono ? "mono" : ""} ${toneCls} text-right truncate max-w-[60%]`}>{v}</dd>
    </div>
  );
}
